'use client';

import { useState } from 'react';
import { Flag, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { ActivityLogger } from '@/lib/activityLog';
import { Toast } from '@/components/ui/Toast';

const baseUrl = '/api/strapi';

type ContentType = 'post' | 'event' | 'group' | 'provider' | 'job' | 'forum' | 'post_comment' | 'forum_reply';
type ReasonType = 'inappropriate_language' | 'harmful_content' | 'spam' | 'self_harm' | 'misinformation' | 'harassment' | 'other';

interface ReportButtonProps {
  contentType: ContentType;
  contentId: string;
  contentTitle: string;
  parentId?: string; // For comments: post ID for post_comment, topic ID for forum_reply
  className?: string;
}

const reasons: ReasonType[] = ['inappropriate_language', 'harmful_content', 'spam', 'self_harm', 'misinformation', 'harassment', 'other'];

export function ReportButton({ contentType, contentId, contentTitle, parentId, className = '' }: ReportButtonProps) {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReasonType | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  if (!currentUser) return null;

  const handleSubmit = async () => {
    if (!selectedReason || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${baseUrl}/content-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            contentType,
            contentId,
            parentId, // For comments: post ID or topic ID
            contentTitle: contentTitle.substring(0, 100),
            reason: selectedReason,
            reporterUserId: currentUser.id,
            reporterName: currentUser.displayName || currentUser.username,
          },
        }),
      });
      if (res.ok) {
        ActivityLogger.flagContent(currentUser, contentType, contentTitle.substring(0, 50), selectedReason, contentId);
        setToast({ message: t.flaggedItems.reportModal.success, type: 'success' });
        setShowModal(false);
        setSelectedReason('');
      } else {
        const data = await res.json();
        setToast({ message: data.error?.message || t.flaggedItems.reportModal.error, type: 'error' });
      }
    } catch (error) {
      console.error('src/components/ui/ReportButton.tsx', error);
      setToast({ message: t.flaggedItems.reportModal.error, type: 'error' });
    }
    setSubmitting(false);
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowModal(true); }}
        className={`text-foreground-muted hover:text-[#c45c5c] transition ${className}`}
        title="Report"
        data-testid="report-trigger-btn"
      >
        <Flag size={14} />
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowModal(false)} data-testid="report-modal-root">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-background rounded-lg shadow-xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-foreground mb-1">{t.flaggedItems.reportModal.title}</h2>
            <p className="text-sm text-foreground-muted mb-4">{t.flaggedItems.reportModal.subtitle}</p>

            <div className="space-y-2 mb-6">
              {reasons.map(reason => (
                <label
                  key={reason}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${selectedReason === reason ? 'border-primary bg-primary/5' : 'border-border hover:bg-sidebar-hover'}`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={() => setSelectedReason(reason)}
                    className="accent-primary"
                    data-testid={`report-modal-reason-${reason.replace(/_/g, '-')}`}
                  />
                  <span className="text-sm text-foreground">{t.flaggedItems.reasons[reason]}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-sidebar-hover" data-testid="report-modal-cancel-btn">
                {t.flaggedItems.reportModal.cancel}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedReason || submitting}
                className="flex-1 px-4 py-2 bg-[#c45c5c] text-white rounded-lg text-sm font-medium hover:bg-[#b54a4a] disabled:opacity-50 transition"
                data-testid="report-modal-submit-btn"
              >
                {submitting ? '...' : t.flaggedItems.reportModal.submit}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

